/**
 * StagePipeline — Visual case lifecycle progress indicator.
 *
 * Shows stages (Setup → Documents → Review → Analysis → Reports)
 * with completion status and current stage highlighted.
 *
 * Props:
 *   stages: Array<{
 *     name?: string,           // legacy field; `title` is preferred
 *     title?: string,          // canonical, matches the punch-list backend
 *     status: 'completed' | 'active' | 'pending'
 *           | 'complete' | 'in_progress' | 'not_started',
 *     detail?: string — e.g., "Complete", "24 files", "In progress"
 *     icon?: ReactNode — icon element
 *   }>
 *   onStageClick: (index) => void — optional click handler
 *
 * The backend (case_stats.py::get_dashboard_workflow_summary) emits
 * `not_started | in_progress | complete`. The legacy hardcoded buildStages()
 * emitted `pending | active | completed`. Both vocabularies are accepted —
 * we normalize at the top of the render.
 */

import React from 'react';
import Card from './Card';
import styles from './StagePipeline.module.css';

const STATUS_ALIAS = {
  not_started: 'pending',
  in_progress: 'active',
  complete: 'completed',
  pending: 'pending',
  active: 'active',
  completed: 'completed',
};

const STATUS_DEFAULT_DETAIL = {
  pending: 'Not started',
  active: 'In progress',
  completed: 'Complete',
};

/* Default stage icons as inline SVGs */
const defaultIcons = {
  completed: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  // Circular arrow (rotate-cw) — signals "in progress / underway"
  active: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 4 21 10 15 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L21 10"/>
    </svg>
  ),
  // Three dots (ellipsis) — signals "queued / more to come"
  pending: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <circle cx="5" cy="12" r="2"/>
      <circle cx="12" cy="12" r="2"/>
      <circle cx="19" cy="12" r="2"/>
    </svg>
  ),
};

function StagePipeline({ stages = [], onStageClick }) {
  return (
    <Card padding="md">
      <div className={styles.pipeline}>
        {stages.map((stage, idx) => {
          const normalizedStatus = STATUS_ALIAS[stage.status] || 'pending';
          const stageName = stage.title || stage.name || '';
          const stageDetail = stage.detail || STATUS_DEFAULT_DETAIL[normalizedStatus];
          return (
            <div
              key={stage.item_id || stage.key || idx}
              className={`${styles.step} ${styles[normalizedStatus]}`}
              onClick={() => onStageClick && onStageClick(idx)}
              role={onStageClick ? 'button' : undefined}
              tabIndex={onStageClick ? 0 : undefined}
            >
              <div className={styles.dot}>
                {normalizedStatus === 'completed' && (stage.icon || defaultIcons.completed)}
                {normalizedStatus === 'active' && (stage.icon || defaultIcons.active)}
                {normalizedStatus === 'pending' && (stage.icon || defaultIcons.pending)}
              </div>
              <div className={styles.info}>
                <div className={styles.name}>{stageName}</div>
                <div className={styles.detail}>{stageDetail}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default StagePipeline;
